import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loading } from '@/components/common';
import ProtectedRoute from './ProtectedRoute';
import { EmployerLayout, CandidateLayout } from '@/components/layout';
import { ROUTES } from './routes';

// Lazy load pages for code splitting

// Auth pages
const LoginPage = lazy(() => import('@/modules/auth/pages/LoginPage'));
const SignupPage = lazy(() => import('@/modules/auth/pages/SignupPage'));
const ResetPasswordPage = lazy(
  () => import('@/modules/auth/pages/ResetPasswordPage')
);

// Employer pages
const DashboardPage = lazy(
  () => import('@/modules/employer/pages/DashboardPage')
);
const CandidatesPage = lazy(
  () => import('@/modules/employer/pages/CandidatesPage')
);
const CandidateResultsPage = lazy(
  () => import('@/modules/employer/pages/CandidateResultsPage')
);
const ViewResultPage = lazy(
  () => import('@/modules/employer/pages/ViewResultPage')
);
const InterviewConfigPage = lazy(
  () => import('@/modules/employer/pages/InterviewConfigPage')
);
const ApiKeysPage = lazy(() => import('@/modules/employer/pages/ApiKeysPage'));
const PlanPage = lazy(() => import('@/modules/employer/pages/PlanPage'));
const SettingsPage = lazy(
  () => import('@/modules/employer/pages/SettingsPage')
);

// Interview Results pages
const InterviewResultsLandingPage = lazy(
  () => import('@/modules/interview-results/pages/InterviewResultsLandingPage')
);
const AllCandidatesPage = lazy(
  () => import('@/modules/interview-results/pages/AllCandidatesPage')
);
const ViewInterviewResultPage = lazy(
  () => import('@/modules/interview-results/pages/ViewInterviewResultPage')
);
// Candidate pages
const InterviewGuidlinesPage = lazy(
  () => import('@/modules/candidate/pages/InterviewGuidlinesPage')
);
const SelectDatePage = lazy(
  () => import('@/modules/candidate/pages/SelectDatePage')
);
const TimeSlotPage = lazy(
  () => import('@/modules/candidate/pages/TimeSlotPage')
);
const ScheduleSuccessPage = lazy(
  () => import('@/modules/candidate/pages/ScheduleSuccessPage')
);

const ThankYouPage = lazy(
  () => import('@/modules/candidate/pages/ThankYouPage')
);

const InstantInterviewPage = lazy(
  () => import('@/modules/candidate/pages/InstantInterviewPage')
);

const LiveInterviewPage = lazy(
  () => import('@/modules/candidate/pages/LiveInterviewPage')
);
const AIInterviewPage = lazy(
  () => import('@/modules/candidate/pages/AIInterviewPage')
);
const InterviewLandingPage = lazy(
  () => import('@/modules/candidate/pages/InterviewLandingPage')
);
const InterviewInstructionsPage = lazy(
  () => import('@/modules/candidate/pages/InterviewInstructionsPage')
);
const InterviewTestingPage = lazy(
  () => import('@/modules/candidate/pages/InterviewTestingPage')
);

// Coding round pages
const CodingRoundInitialPage = lazy(
  () => import('@/modules/coding-round/pages/CodingRoundInitialPage')
);
const CodingRoundPage = lazy(
  () => import('@/modules/coding-round/pages/CodingRoundPage')
);
const CodingRoundResultPage = lazy(
  () => import('@/modules/coding-round/pages/CodingRoundResultPage')
);

// Error pages
const NotFoundPage = lazy(() => import('@/modules/common/pages/NotFoundPage'));
const UnauthorizedPage = lazy(
  () => import('@/modules/common/pages/UnauthorizedPage')
);

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<Loading fullScreen text="Loading..." />}>
      <Routes>
        {/* Public Auth Routes */}
        <Route path={ROUTES.login} element={<LoginPage />} />
        <Route path={ROUTES.signUp} element={<SignupPage />} />
        <Route path={ROUTES.resetPassword} element={<ResetPasswordPage />} />

        {/* Employer Routes (Protected) */}
        <Route
          path={ROUTES.employer}
          element={
            <ProtectedRoute allowedRoles={['admin', 'employer']}>
              <EmployerLayout />
            </ProtectedRoute>
          }
        >
          <Route
            index
            element={<Navigate to={ROUTES.employerDashboard} replace />}
          />
          <Route path={ROUTES.employerDashboard} element={<DashboardPage />} />
          <Route
            path={ROUTES.employerCandidates}
            element={<CandidatesPage />}
          />
          <Route
            path={ROUTES.employerCandidateResults}
            element={<CandidateResultsPage />}
          />
          <Route
            path={ROUTES.employerViewResult}
            element={<ViewResultPage />}
          />
          <Route
            path={ROUTES.employerInterviewConfig}
            element={<InterviewConfigPage />}
          />
          <Route path={ROUTES.employerApiKeys} element={<ApiKeysPage />} />
          <Route path={ROUTES.employerPlan} element={<PlanPage />} />
          <Route path={ROUTES.employerSettings} element={<SettingsPage />} />
        </Route>

        {/* Candidate Routes */}
        <Route element={<CandidateLayout />}>
          <Route
            path={ROUTES.initialRoute}
            element={<InterviewLandingPage />}
          />
          <Route
            path={ROUTES.interviewGuidelines}
            element={<InterviewGuidlinesPage />}
          />
          <Route path={ROUTES.selectDate} element={<SelectDatePage />} />
          <Route path={ROUTES.timeSlot} element={<TimeSlotPage />} />
          <Route
            path={ROUTES.scheduleSuccess}
            element={<ScheduleSuccessPage />}
          />

          <Route path={ROUTES.thankYou} element={<ThankYouPage />} />
          {/* New Interview Flow Routes */}
          <Route
            path={ROUTES.interviewLanding}
            element={<InterviewLandingPage />}
          />
          <Route
            path={ROUTES.interviewInstructions}
            element={<InterviewInstructionsPage />}
          />
          <Route
            path={ROUTES.interviewTesting}
            element={<InterviewTestingPage />}
          />
        </Route>

        {/* Full screen interview routes (no layout) */}
        <Route path={ROUTES.liveInterview} element={<LiveInterviewPage />} />
        <Route path={ROUTES.aiInterview} element={<AIInterviewPage />} />
        <Route
          path={ROUTES.instantInterview}
          element={<InstantInterviewPage />}
        />

        {/* New Interview Results module routes */}
        <Route
          path={ROUTES.interviewResultsRoot}
          element={<InterviewResultsLandingPage />}
        />
        <Route
          path={ROUTES.interviewResultsAllCandidates}
          element={<AllCandidatesPage />}
        />
        <Route
          path={ROUTES.interviewResultsView}
          element={<ViewInterviewResultPage />}
        />

        {/* Coding Round Routes */}
        <Route
          path={ROUTES.codingRoundInitial}
          element={<CodingRoundInitialPage />}
        />
        <Route path={ROUTES.codingRound} element={<CodingRoundPage />} />
        <Route
          path={ROUTES.codingRoundResult}
          element={<CodingRoundResultPage />}
        />

        {/* Error Routes */}
        <Route path={ROUTES.unauthorized} element={<UnauthorizedPage />} />
        <Route path={ROUTES.notFound} element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
